import { useEffect, useState, useCallback } from "react";
import { Link, useSearch } from "wouter";
import { CheckCircle2, Clock, AlertCircle, ArrowRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2000;

/** Polls the payment status endpoint until paid / expired / timeout / error. */
function usePaymentStatus(sessionId) {
  const [state, setState] = useState({
    status: sessionId ? "polling" : "no-session",
    kind: null,
    amount: 0,
    currency: "usd",
    error: "",
    attempts: 0,
  });

  const poll = useCallback(async (attempt) => {
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setState({
          status: "paid",
          kind: data.kind,
          amount: (data.amount_total || 0) / 100,
          currency: data.currency || "usd",
          error: "",
          attempts: attempt,
        });
        return;
      }
      if (data.status === "expired") {
        setState((s) => ({ ...s, status: "expired", attempts: attempt }));
        return;
      }
      if (attempt + 1 >= MAX_POLLS) {
        setState((s) => ({ ...s, status: "timeout", attempts: attempt }));
        return;
      }
      setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
    } catch (e) {
      setState((s) => ({
        ...s,
        status: "error",
        error: e.response?.data?.detail || "Couldn't confirm payment status.",
        attempts: attempt,
      }));
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) poll(0);
  }, [sessionId, poll]);

  return state;
}

export default function PaymentSuccess() {
  const search = useSearch();
  const sessionId = new URLSearchParams(search).get("session_id");
  const state = usePaymentStatus(sessionId);

  return (
    <div data-testid="page-payment-success" className="bg-spa-cream min-h-[80vh] flex items-center">
      <div className="container-spa py-24">
        <div className="max-w-xl mx-auto text-center card-spa">
          {state.status === "polling" && <PollingView />}
          {state.status === "paid" && <PaidView state={state} sessionId={sessionId} />}
          {(state.status === "timeout" || state.status === "error" || state.status === "expired") && (
            <FallbackView status={state.status} error={state.error} />
          )}
          {state.status === "no-session" && <NoSessionView />}
        </div>
      </div>
    </div>
  );
}

const PollingView = () => (
  <>
    <div className="w-16 h-16 rounded-full bg-spa-sand flex items-center justify-center mx-auto mb-6">
      <Clock className="w-7 h-7 text-spa-gray animate-pulse" strokeWidth={1.5} />
    </div>
    <p className="overline mb-3">Confirming your payment</p>
    <h1 className="font-serif text-3xl sm:text-4xl text-spa-dark mb-4">Just a moment…</h1>
    <p className="text-spa-gray">We&apos;re finalizing things with Stripe — this usually takes a few seconds.</p>
  </>
);

const PaidView = ({ state, sessionId }) => (
  <>
    <div className="w-16 h-16 rounded-full bg-spa-green/10 flex items-center justify-center mx-auto mb-6">
      <CheckCircle2 className="w-8 h-8 text-spa-green" strokeWidth={1.5} />
    </div>
    <p className="overline text-spa-gold mb-3">Payment confirmed</p>
    <h1 className="font-serif text-4xl sm:text-5xl text-spa-dark leading-tight mb-5">Thank you.</h1>
    <p className="text-spa-gray leading-relaxed mb-8">
      {state.kind === "booking"
        ? <>Your <span className="text-spa-dark font-medium">${state.amount.toFixed(2)} deposit</span> is received and your session is reserved. The remaining balance is paid directly to your therapist at the appointment. Stephanie will reach out within 24 hours to finalize the date and address.</>
        : <>Your gift card for <span className="text-spa-dark font-medium">${state.amount.toFixed(2)}</span> has been issued. A confirmation will be sent to the recipient&apos;s email shortly.</>}
    </p>
    <div className="bg-spa-sand rounded-xl p-5 mb-8 text-left">
      <p className="overline mb-2">Order Reference</p>
      <p data-testid="success-session-id" className="font-mono text-xs text-spa-dark break-all">{sessionId}</p>
    </div>
    <Link href="/" data-testid="success-home-link">
      <button className="btn-primary">Return home <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></button>
    </Link>
  </>
);

function fallbackMessage(status, error) {
  if (status === "error") return error;
  if (status === "expired") return "No charge was made. You can start a new checkout below.";
  return "We're still waiting on confirmation from Stripe. If your card was charged, your booking is safe — Stephanie will see it in the dashboard. Refresh this page in a minute or contact us at masonmassage865@gmail.com.";
}

const FallbackView = ({ status, error }) => (
  <>
    <div className="w-16 h-16 rounded-full bg-spa-sand flex items-center justify-center mx-auto mb-6">
      <AlertCircle className="w-8 h-8 text-spa-gray" strokeWidth={1.5} />
    </div>
    <p className="overline mb-3">{status === "expired" ? "Session expired" : "Still confirming"}</p>
    <h1 className="font-serif text-3xl sm:text-4xl text-spa-dark mb-4">
      {status === "expired" ? "This checkout has expired." : "Hang tight."}
    </h1>
    <p className="text-spa-gray leading-relaxed mb-8">{fallbackMessage(status, error)}</p>
    <div className="flex flex-wrap gap-3 justify-center">
      <Link href="/" data-testid="success-fallback-home">
        <button className="btn-secondary">Return home</button>
      </Link>
      <Link href="/services" data-testid="success-fallback-services">
        <button className="btn-primary">Back to services</button>
      </Link>
    </div>
  </>
);

const NoSessionView = () => (
  <>
    <div className="w-16 h-16 rounded-full bg-spa-sand flex items-center justify-center mx-auto mb-6">
      <AlertCircle className="w-8 h-8 text-spa-gray" strokeWidth={1.5} />
    </div>
    <h1 className="font-serif text-3xl sm:text-4xl text-spa-dark mb-4">No order found.</h1>
    <p className="text-spa-gray mb-8">It looks like you landed here directly. Start from the home page.</p>
    <Link href="/" data-testid="success-no-session-home">
      <button className="btn-primary">Return home</button>
    </Link>
  </>
);
